<script setup>
import PencilIcon from "@/components/Icons/PencilIcon.vue";
import {t} from "@/controllerLK/GlobusLocalization";
import TagIcon from "@/components/Icons/TagIcon.vue";
import AccordionList from "@/components/NewsPage/NewsPageBody/NewsPageBodyPost/AccordionList.vue";
import {computed, ref, defineProps} from "vue";
import {useNewsFeedStore} from "@/store/NewsFeed/newsFeed";
import {useLK} from "@/controllerLK/LKstore";
import NewsPagePopupSidebar from "@/components/NewsPage/NewsPagePopup/NewsPagePopupSidebar.vue";
import CategorySwitcher from "@/components/NewsPage/NewsPageBody/NewsPageBodyPost/CategorySwitcher.vue";
import {useRubricsStore} from "@/store/NewsFeed/rubrics";
import SentimentSwitcher from "@/components/NewsPage/NewsPageBody/NewsPageBodyPost/SentimentSwitcher.vue";
import {useBlockStore} from "@/store/NewsFeed/blocks";
import NewsPageObjectList from "@/components/NewsPage/NewsPageBody/NewsPageBodyPost/NewsPageObjectList.vue";
import {useNotificationsStore} from "@/store/notifications";

const blockStore = useBlockStore();
const rubricsStore = useRubricsStore();
const newsFeedStore = useNewsFeedStore();
const LKstore = useLK();
const notificationsStore = useNotificationsStore();

const props = defineProps({
  isShowFullNews: {
    type: Boolean,
    default: false,
  },
});

// Теги
// Флаг отображения сайдбара изменения тегов.
const isShowEditTagsSidebar = ref(false);

// Функция переключения отображения сайдбара изменения тегов
const toggleShowEditTagsSidebar = () => {
  isShowEditTagsSidebar.value = !isShowEditTagsSidebar.value;
};

// Список тегов
const filtredTags = computed(() => {
  return newsFeedStore.onFilterTags.filter((tag) => {
    return newsFeedStore.currentNews?.source_tag_ids &&
      newsFeedStore.currentNews?.source_tag_ids.find((filteredTag) =>
        String(filteredTag) === String(tag.id)
      );
  });
});

// Список наименований тегов
const filterTagsName = computed(() => filtredTags.value.map((item) => item.name));

// Функция сохранения тегов карточки
const saveTags = async (items) => {
  await newsFeedStore.updateCardTags(newsFeedStore.currentNews.id, items.value);
  isShowEditTagsSidebar.value = false;
};

// Категории
// Список наименований категорий
const categoriesName = computed(() => {

  const categoryName = newsFeedStore.filterCategories.list.find((val) => val.id ===
      newsFeedStore.currentNews.category_id)?.name;

  if (categoryName) {
    return [categoryName];
  } else {
    return [];
  }
});

// Флаг отображения сайдбара редактирования категории
const isShowEditCategorySwitcher = ref(false);

// Функция. Переключение отображение сайдбара редактирования категорий
const toggleShowEditCategorySwitcher = () => {
  isShowEditCategorySwitcher.value = !isShowEditCategorySwitcher.value;
};

// Рубрики.
// Флаг отображения сайдбара редактирования рубрик
const isShowEditRubricsSidebar = ref(false);

// Список рубрик
const rubricsList = computed(() =>
    rubricsStore.allRubrics.filter((r) => newsFeedStore.currentNews.rubric_ids.includes(r.id))
);
// Список наименований рубрик
const rubricNameList = computed(() => rubricsList.value.map((r) => r.name));

// Функция сохранения рубрик
const saveRubrics = async (items) => {
  await newsFeedStore.updateCardRubrics(newsFeedStore.currentNews.id, items.value);
  isShowEditRubricsSidebar.value = false;
};

// Темы
// Список тем текущий новости
const currentPostThemes = computed(
    () => blockStore.allThemes.filter((item) => newsFeedStore.currentNews.block_ids.includes(item.id)));
// Список наименований тем текущей новости
const currentPostThemesName = computed(() => currentPostThemes.value.map((item) => item.name));

// Объекты
// Список наименований объектов NER Result.
const objectsNameList = computed(() => {
  let result = [];
  if (newsFeedStore.currentNews && newsFeedStore.currentNews.ner_result) {
    if (newsFeedStore.currentNews.ner_result.persons) {
      result = result.concat(newsFeedStore.currentNews.ner_result.persons);
    }
    // if (newsFeedStore.currentNews.ner_result.locations) {
    //   result = result.concat(newsFeedStore.currentNews.ner_result.locations);
    // }
    // if (newsFeedStore.currentNews.ner_result.organisations) {
    //   result = result.concat(newsFeedStore.currentNews.ner_result.organisations);
    // }
  }
  return result;
});


// Форматирование числа с разделением тысяч пробелом
const formatNumber = (num) => {
  if (!num) return "";
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

// Сохранения ID новости в буфер при клике.
const saveIdToClipboard = async (id) => {
  try {
    await navigator.clipboard.writeText("id: " + id);
    notificationsStore.createNotify(t("map_page.toast.clipboard_success"), "success");
  } catch (e) {
    console.log(e);
    notificationsStore.createNotify(t("map_page.toast.clipboard_error"), "success");
  }
};
</script>

<template>
  <div class="body-post__footer">
    <template v-if="newsFeedStore.currentNews.rubric_ids.length > 0">
      <!--     Теги      -->
      <AccordionList
        :initially-expanded="props.isShowFullNews"
        :use-trigger="LKstore.user.roleNames.includes('аналитик')"
        :title-icon="PencilIcon"
        :label="t('news_page_body_post.label.tag')"
        :tag-icon="TagIcon"
        :list="filterTagsName"
        :not-defined-text="t('news_page_body_post.label.not_defined_set')"
        @trigger="toggleShowEditTagsSidebar"
      />
      <!--     Категория      -->
      <AccordionList
        :label="t('news_page_body_post.label.category')"
        :list="categoriesName"
        :use-not-defined="true"
        :use-trigger="LKstore.user.roleNames.includes('аналитик')"
        :title-icon="PencilIcon"
        @trigger="toggleShowEditCategorySwitcher"
      />

      <!--     Сантимент      -->

      <SentimentSwitcher />

      <!--     Рубрики      -->
      <AccordionList
        :initially-expanded="props.isShowFullNews"
        :title-icon="PencilIcon"
        :use-trigger="LKstore.user.roleNames.includes('аналитик')"
        :label="t('news_page_body_post.label.rubric')"
        :not-defined-text="t('news_page_body_post.label.not_defined_set')"
        :list="rubricNameList"
        @trigger="isShowEditRubricsSidebar = !isShowEditRubricsSidebar"
      />
      <!--     Темы      -->
      <AccordionList
        :label="t('news_page_body_post.label.theme')"
        :list="currentPostThemesName"
        :not-defined-text="t('news_page_body_post.label.not_defined_set')"
      />
      <!--     Объекты      -->
      <NewsPageObjectList
        v-if="!props.isShowFullNews"
        :list="objectsNameList"
        :is-show-full-news="props.isShowFullNews"
      />
    </template>

    <!--    Источники      -->
    <AccordionList
      :label="t('news_page_body_post.label.source')"
      :list="[newsFeedStore.currentNews.url]"
      accordion-item-type="url"
      :not-defined-text="t('news_page_body_post.label.not_defined_set')"
    />
    <!--    ID Новости. Для полноэкранного режима      -->
    <AccordionList
      v-if="props.isShowFullNews"
      :label="`${t('news_page_body_post.label.id_news')}:`"
      :list="[newsFeedStore.currentNews.id]"
      accordion-item-type="default"
      :not-defined-text="t('news_page_body_post.label.not_defined_set')"
      @click="saveIdToClipboard(newsFeedStore.currentNews.id)"
    />
    <p class="body-post__footer__visits">
      <span>{{ t('news_page_body_post.label.visits') }}</span> 
      <span 
        v-if="newsFeedStore.currentNews.count_visits_monthly" 
        class="body-post__footer__visits-cont"
      >
        {{ formatNumber(newsFeedStore.currentNews.count_visits_monthly) }}
      </span>
      <span 
        v-else
        class="body-post__footer__not_defined"
      >
        {{ t('news_page_body_post.label.not_defined_set') }}
      </span>
    </p>
  </div>

  <!-- Сайдбар редактирования тегов -->
  <NewsPagePopupSidebar
    v-if="isShowEditTagsSidebar"
    :items="newsFeedStore.onFilterTags"
    :selected-items="newsFeedStore.currentNews.source_tag_ids"
    :title="t('news_page_popup_sidebar_tag.title')"
    @save-changes="(items) => saveTags(items)"
    @close="isShowEditTagsSidebar = false"
  />

  <!-- Сайдбар редактирования категорий -->
  <CategorySwitcher
    v-if="isShowEditCategorySwitcher"
    :title="t('category_switcher.title')"
    @close="toggleShowEditCategorySwitcher"
  />

  <!-- Сайдбар редактирования рубрик -->
  <NewsPagePopupSidebar
    v-if="isShowEditRubricsSidebar"
    :items="rubricsStore.allRubrics.filter((filteredRubric) => filteredRubric.is_fixed)"
    :selected-items="newsFeedStore.currentNews.rubric_ids"
    :title="t('news_page_popup_sidebar_rubric.title')"
    @save-changes="(items) => saveRubrics(items)"
    @close="isShowEditRubricsSidebar = false"
  />
</template>

<style scoped lang="scss">

.body-post {
  &__footer {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 20px;

    &__visits {
      font-weight: 400;
      color: #ADB6CC;

      & span {
        color: #7B87A5;
        font-weight: 500;
        margin-right: 20px;
      }
    }

    &__not_defined {
      color: rgba(123, 135, 165, 0.3) !important;
    }
  }

  &__source {
    margin-top: 20px;
  }
}

</style>